import React from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import { Send } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';

interface Customer {
  id: string;
  email: string;
  profile: any;
  isVip: boolean;
}

interface CampaignForm {
  name: string;
  subject: string;
  content: string;
  recipients: string[];
}

interface CampaignDialogProps {
  open: boolean;
  onClose: () => void;
  form: CampaignForm;
  setForm: (form: CampaignForm) => void;
  customers: Customer[];
  onSend: () => Promise<void>;
}

const CampaignDialog: React.FC<CampaignDialogProps> = ({
  open,
  onClose,
  form,
  setForm,
  customers,
  onSend,
}) => {
  const { t } = useTranslation();

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: '8px',
          bgcolor: 'background.paper',
        },
      }}
    >
      <DialogTitle>{t('admin.createEmailCampaign')}</DialogTitle>
      <DialogContent>
        <Box sx={{ pt: 2 }}>
          <TextField
            fullWidth
            label={t('admin.campaignName')}
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            sx={{ mb: 3 }}
          />
          <TextField
            fullWidth
            label={t('admin.subjectLine')}
            value={form.subject}
            onChange={(e) => setForm({ ...form, subject: e.target.value })}
            sx={{ mb: 3 }}
          />
          <TextField
            fullWidth
            multiline
            rows={6}
            label={t('admin.emailContent')}
            value={form.content}
            onChange={(e) => setForm({ ...form, content: e.target.value })}
            sx={{ mb: 3 }}
          />
          <FormControl fullWidth>
            <InputLabel>{t('admin.selectRecipients')}</InputLabel>
            <Select
              multiple
              value={form.recipients}
              onChange={(e) => setForm({ ...form, recipients: e.target.value as string[] })}
              renderValue={(selected) => t('admin.recipientsSelected', { count: selected.length })}
            >
              {customers.map((customer) => (
                <MenuItem key={customer.id} value={customer.email}>
                  {customer.profile?.full_name || customer.email} 
                  {customer.isVip && ' (VIP)'}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t('common.cancel')}</Button>
        <Button 
          onClick={onSend} 
          variant="contained"
          startIcon={<Send />}
          sx={{
            background: 'linear-gradient(135deg, #00ff88 0%, #00cc55 100%)',
            '&:hover': {
              background: 'linear-gradient(135deg, #00cc55 0%, #009944 100%)',
            },
          }}
        >
          {t('admin.sendCampaign')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CampaignDialog;